import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { SITE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_PROMPTS = [
  "Dholera Smart City kya hai?",
  "Residential plots available hain?",
  "Site visit kaise karu?",
  "What is the expressway status?",
];

function getSessionId() {
  let id = localStorage.getItem("pre_chat_sid");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("pre_chat_sid", id);
  }
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        if (chunk) {
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            copy[copy.length - 1] = { ...last, content: last.content + chunk };
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: `Sorry, I could not respond right now. Please call or WhatsApp us at ${SITE.phoneDisplay}.`,
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed left-4 bottom-24 z-50 w-[calc(100vw-2rem)] max-w-sm h-[540px] max-h-[72vh] bg-white border border-gold/40 shadow-deep rounded-sm flex flex-col overflow-hidden"
            data-testid="chat-panel"
          >
            <div className="bg-navy px-5 py-4 flex items-center gap-3 shrink-0">
              <span className="w-9 h-9 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-gold" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-white font-semibold leading-tight">Wingman</p>
                <p className="text-white/55 text-[11px] font-mono uppercase tracking-[0.14em]">AI Assistant · English · हिंदी · Hinglish</p>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close-button" className="text-white/60 hover:text-gold transition-colors duration-300 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-paper" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="space-y-3">
                  <div className="bg-white border border-border rounded-sm px-4 py-3 text-sm text-body shadow-sm">
                    Namaste! Ask me anything about Dholera Smart City or our property services.
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_PROMPTS.map((q) => (
                      <button
                        key={q}
                        onClick={() => send(q)}
                        data-testid="chat-quick-prompt"
                        className="text-xs font-medium text-navy border border-gold/50 bg-gold-light px-3 py-1.5 rounded-full hover:bg-gold hover:text-navy transition-colors duration-300"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rounded-sm whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-navy text-white"
                        : "bg-white border border-border text-body shadow-sm"
                    }`}
                  >
                    {m.content || <span className="inline-flex gap-1 py-1"><span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce" /><span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce [animation-delay:0.15s]" /><span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce [animation-delay:0.3s]" /></span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="shrink-0 border-t border-border bg-white px-3 py-3">
              <form
                className="flex gap-2"
                onSubmit={(e) => { e.preventDefault(); send(); }}
              >
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your question…"
                  data-testid="chat-input"
                  maxLength={500}
                  className="flex-1 min-w-0 bg-paper border border-border px-3.5 py-2.5 text-sm text-navy placeholder:text-navy/40 outline-none focus:border-gold transition-colors duration-300 rounded-sm"
                />
                <button
                  type="submit"
                  disabled={busy || !input.trim()}
                  aria-label="Send message"
                  data-testid="chat-send-button"
                  className="w-11 h-11 shrink-0 bg-gold text-navy flex items-center justify-center rounded-sm hover:bg-gold-bright transition-colors duration-300 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
              <p className="mt-2 text-[10px] text-navy/40 text-center font-mono uppercase tracking-[0.12em]">
                AI assistant · Verify details with our team
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Open AI chat assistant"
        data-testid="chat-open-button"
        className="fixed left-4 bottom-5 z-50 w-14 h-14 rounded-full bg-navy text-white border border-gold/60 shadow-deep flex items-center justify-center hover:scale-110 transition-transform duration-300"
        style={{ bottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!open && (
          <span className="absolute -top-1 -right-1 bg-gold text-navy font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-full">AI</span>
        )}
      </button>
    </>
  );
}
